import * as http from 'node:http';
import * as https from 'node:https';
import * as net from 'node:net';
import type { AddressInfo, Socket } from 'node:net';
import { PassThrough, type Duplex } from 'node:stream';
import { randomUUID } from 'node:crypto';
import { URL } from 'node:url';
import { createBrotliDecompress, createGunzip, createInflate } from 'node:zlib';
import type { ProxyHandle, ProxyRoute } from '../proxy.js';
import { startProxyCatalog } from '../proxy.js';
import { decodeRequestBody } from '../http-utils.js';
import { ensureHttpProxyCertificates } from './ca.js';
import { normalizeRouteLookupId } from '../context-model-id.js';
import { listenTcpServer } from '../listener-ready.js';
import { routeUnavailableMessage } from '../route-unavailable.js';
import { passthroughUpstreamRetries } from '../upstream-retry.js';
import {
  outboundHttpProxyAgent,
  outboundProxyUrlForTarget,
  proxyUrlTargetsListener,
} from '../outbound-proxy.js';
import { HTTP_PROXY_MODEL_PREFIX, type ResolvedHttpProxyAlias } from './routes.js';
import { anthropicEffortFromRequest, extractClaudeSessionId, type AnthropicRequest } from '../sdk-adapter.js';
import { anthropicMessagesEndpoint } from '../anthropic-endpoints.js';
import { isOpenAiOAuthRoute, oauthServiceTier } from '../sdk-adapter.js';
import {
  getLatestMessagePreview,
  INFERENCE_PROGRESS_INTERVAL_MS,
  writeInferenceRequestLog,
  writeInferenceRouteUnavailableLog,
  writeInferenceResponseLifecycleLog,
  writeInferenceResponseErrorLog,
  writeWebSocketDiagnosticRequestLog,
  type InferenceFailureSource,
  type InferenceResponsePhase,
} from '../trace-log.js';

const ANTHROPIC_HOST = 'api.anthropic.com';
const MAX_BODY_BYTES = 50 * 1024 * 1024;
const MAX_ERROR_BODY_BYTES = 64 * 1024;
const MAX_USAGE_SSE_BLOCK_BYTES = 64 * 1024;

/**
 * The reset a pooled socket produces when the peer closed it while it sat idle.
 * EPIPE is arguably as safe to replay and is deliberately NOT included: no test
 * here can stage one, and an untested replay path on a billable POST is not
 * worth the reach. `errorType` on response_failed records it if it shows up.
 */
const RETRYABLE_PASSTHROUGH_CODE = 'ECONNRESET';

/**
 * The passthrough's own connection pool. `timeout` must not be dropped: it is
 * what evicts an IDLE pooled socket, and https.globalAgent -- which this
 * replaces -- carries one. Without it dead sockets accumulate in the pool and
 * the stale-socket reset this module absorbs becomes MORE likely, not less.
 */
/**
 * Identify an upstream failure for the 502 body. Observed live: an ETIMEDOUT
 * whose `message` was empty, rendering as "Anthropic upstream unreachable: ."
 * -- exactly the unactionable error this path exists to stop producing. Fall
 * back to whatever does identify it.
 */
export function upstreamUnreachableDetail(err: Error): string {
  return err.message || (err as NodeJS.ErrnoException).code || err.name || 'connection failed';
}

export function createPassthroughAgent(): https.Agent {
  return new https.Agent({
    keepAlive: true,
    timeout: https.globalAgent.options.timeout ?? 5_000,
  });
}

type ResponseUsage = {
  usageStage: 'message_start' | 'message_delta';
  inputTokens?: number;
  outputTokens?: number;
  cacheCreationInputTokens?: number;
  cacheReadInputTokens?: number;
};

function numericUsage(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function responseUsageFromSseBlock(block: string): ResponseUsage | undefined {
  const lines = block.split('\n');
  const event = lines.find(line => line.startsWith('event:'))?.slice('event:'.length).trim();
  const data = lines
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice('data:'.length).trimStart())
    .join('\n');
  if (!data) return undefined;

  try {
    const parsed = JSON.parse(data) as Record<string, unknown>;
    const type = parsed.type;
    if (type !== 'message_start' && type !== 'message_delta') return undefined;
    if (event && event !== type) return undefined;
    const message = type === 'message_start'
      ? parsed.message as Record<string, unknown> | undefined
      : undefined;
    const usage = (type === 'message_start' ? message?.usage : parsed.usage) as Record<string, unknown> | undefined;
    if (!usage) return undefined;
    return {
      usageStage: type,
      inputTokens: numericUsage(usage.input_tokens),
      outputTokens: numericUsage(usage.output_tokens),
      cacheCreationInputTokens: numericUsage(usage.cache_creation_input_tokens),
      cacheReadInputTokens: numericUsage(usage.cache_read_input_tokens),
    };
  } catch {
    return undefined;
  }
}

function createResponseUsageCapture(
  onUsage: (usage: ResponseUsage) => void,
): (chunk: Buffer) => void {
  let buffered = '';

  return chunk => {
    buffered = (buffered + chunk.toString('utf8')).replace(/\r\n/g, '\n');

    let boundary: number;
    while ((boundary = buffered.indexOf('\n\n')) >= 0) {
      const block = buffered.slice(0, boundary);
      buffered = buffered.slice(boundary + 2);
      if (Buffer.byteLength(block) > MAX_USAGE_SSE_BLOCK_BYTES) continue;
      const usage = responseUsageFromSseBlock(block);
      if (usage) onUsage(usage);
    }

    // Usage events are tiny. Drop an oversized unterminated event rather than
    // retaining arbitrary streamed response content in this observer.
    if (Buffer.byteLength(buffered) > MAX_USAGE_SSE_BLOCK_BYTES) buffered = '';
  };
}

function observeResponseUsage(
  upstream: http.IncomingMessage,
  contentEncoding: string | string[] | undefined,
  onUsage: (usage: ResponseUsage) => void,
): void {
  const encoding = (Array.isArray(contentEncoding) ? contentEncoding[0] : contentEncoding)
    ?.trim()
    .toLowerCase();
  if (!encoding || encoding === 'identity') {
    const capture = createResponseUsageCapture(onUsage);
    upstream.on('data', capture);
    upstream.once('end', () => upstream.off('data', capture));
    return;
  }

  const decoder = encoding === 'gzip'
    ? createGunzip()
    : encoding === 'br'
      ? createBrotliDecompress()
      : encoding === 'deflate'
        ? createInflate()
        : undefined;
  if (!decoder) return;

  const onCompressedData = (chunk: Buffer) => {
    if (!decoder.destroyed) decoder.write(chunk);
  };
  const onCompressedEnd = () => {
    if (!decoder.destroyed) decoder.end();
  };
  const cleanup = () => {
    upstream.off('data', onCompressedData);
    upstream.off('end', onCompressedEnd);
    decoder.destroy();
  };
  const capture = createResponseUsageCapture(onUsage);
  decoder.on('data', capture);
  decoder.once('error', cleanup);
  decoder.once('end', cleanup);
  upstream.on('data', onCompressedData);
  upstream.once('end', onCompressedEnd);
}

export interface HttpProxyOptions {
  host?: string;
  port?: number;
  routes: ProxyRoute[];
  /** Short incoming model names mapped to canonical adapter route ids. */
  modelAliases?: ResolvedHttpProxyAlias[];
  /** Configured local model ids that must never fall through to Anthropic. */
  reservedModelIds?: string[];
  debug?: boolean;
  /** Per-process translated-adapter debug log used when debug is enabled. */
  debugLogPath?: string;
  /** Append privacy-minimal inference routing records as JSONL. */
  inferenceLogPath?: string;
  /** Opt-in request-envelope and WebSocket head-decision diagnostics. */
  webSocketDiagnosticsLogPath?: string;
  /** Test hook; production always uses https://api.anthropic.com. */
  anthropicOrigin?: string;
  /** Test hook for a local self-signed Anthropic origin. */
  anthropicRejectUnauthorized?: boolean;
  /** Test hook for observing relay-route isolation without calling an AI provider. */
  adapterHandle?: ProxyHandle;
  /** Test hook for exercising adapter request transport failures. */
  adapterRequest?: typeof http.request;
  /** Test hook; production emits a progress record every 30 seconds. */
  responseProgressIntervalMs?: number;
}

export interface HttpProxyHandle {
  host: string;
  port: number;
  caCertPath: string;
  modelIds: string[];
  inferenceLogPath?: string;
  webSocketDiagnosticsLogPath?: string;
  close: () => Promise<void>;
}

function authorityParts(authority: string): { host: string; port: number } | null {
  try {
    const parsed = new URL(`http://${authority}`);
    return { host: parsed.hostname, port: Number(parsed.port || 443) };
  } catch {
    return null;
  }
}

export function shouldInterceptConnect(authority: string): boolean {
  const target = authorityParts(authority);
  return Boolean(target && target.port === 443 && target.host.replace(/\.$/, '').toLowerCase() === ANTHROPIC_HOST);
}

function readRawBody(req: http.IncomingMessage): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    let size = 0;
    req.on('data', (chunk: Buffer) => {
      size += chunk.length;
      if (size > MAX_BODY_BYTES) {
        reject(new Error('Request body too large'));
        req.destroy();
        return;
      }
      chunks.push(chunk);
    });
    req.on('end', () => resolve(Buffer.concat(chunks)));
    req.on('error', reject);
  });
}

function copyResponse(
  upstream: http.IncomingMessage,
  res: http.ServerResponse,
  onErrorResponse?: (statusCode: number, body: string) => void,
  onResponseUsage?: (usage: ResponseUsage) => void,
): void {
  const statusCode = upstream.statusCode ?? 502;
  const contentType = upstream.headers['content-type'];
  if (statusCode < 400 && onResponseUsage && typeof contentType === 'string' && contentType.includes('text/event-stream')) {
    observeResponseUsage(upstream, upstream.headers['content-encoding'], onResponseUsage);
  }
  const errorChunks: Buffer[] = [];
  let capturedBytes = 0;
  let truncated = false;
  let errorLogged = false;
  const logErrorResponse = (suffix = '') => {
    if (errorLogged || statusCode < 400 || !onErrorResponse) return;
    errorLogged = true;
    const body = Buffer.concat(errorChunks).toString('utf8');
    onErrorResponse(statusCode, `${body}${truncated ? ' [truncated]' : ''}${suffix}`);
  };
  if (statusCode >= 400 && onErrorResponse) {
    upstream.on('data', (chunk: Buffer) => {
      if (capturedBytes >= MAX_ERROR_BODY_BYTES) {
        truncated = true;
        return;
      }
      const available = MAX_ERROR_BODY_BYTES - capturedBytes;
      const captured = chunk.length > available ? chunk.subarray(0, available) : chunk;
      errorChunks.push(Buffer.from(captured));
      capturedBytes += captured.length;
      if (captured.length < chunk.length) truncated = true;
    });
    upstream.once('end', () => logErrorResponse());
  }
  res.writeHead(statusCode, upstream.statusMessage, upstream.rawHeaders);
  upstream.once('error', err => {
    logErrorResponse(` [stream error: ${err.message}]`);
    res.destroy();
  });
  upstream.pipe(res);
}

function requestHeadersWithoutProxyHeaders(req: http.IncomingMessage): string[] {
  const headers: string[] = [];
  for (let i = 0; i < req.rawHeaders.length; i += 2) {
    const name = req.rawHeaders[i]!;
    if (/^proxy-(authorization|connection)$/i.test(name)) continue;
    headers.push(name, req.rawHeaders[i + 1] ?? '');
  }
  return headers;
}

function forwardRawAnthropicRequest(
  req: http.IncomingMessage,
  res: http.ServerResponse,
  rawBody: Buffer,
  origin: URL,
  rejectUnauthorized: boolean,
  agent?: https.Agent,
  onErrorResponse?: (statusCode: number, body: string) => void,
  onResponseUsage?: (usage: ResponseUsage) => void,
  lifecycle?: {
    logPath: string;
    requestId: string;
    claudeSessionId?: string;
    modelId: string;
    provider: string;
    progressIntervalMs: number;
  },
  isLocalShutdown: () => boolean = () => false,
): Promise<void> {
  return new Promise(resolve => {
    const startedAt = Date.now();
    const retryBudget = passthroughUpstreamRetries();
    let lastActivityAt = startedAt;
    let headersReceived = false;
    let firstByteAt: number | undefined;
    let statusCode: number | undefined;
    let bytes = 0;
    let chunks = 0;
    let settled = false;
    let responseEnded = false;
    let failed = false;
    let clientDisconnected = false;
    const writeLifecycle = (
      event: Parameters<typeof writeInferenceResponseLifecycleLog>[1]['event'],
      extra: Partial<Parameters<typeof writeInferenceResponseLifecycleLog>[1]> = {},
    ) => {
      if (!lifecycle) return;
      writeInferenceResponseLifecycleLog(lifecycle.logPath, {
        event,
        requestId: lifecycle.requestId,
        claudeSessionId: lifecycle.claudeSessionId,
        modelId: lifecycle.modelId,
        provider: lifecycle.provider,
        route: 'passthrough',
        ...extra,
      });
    };
    const responsePhase = (): InferenceResponsePhase => {
      if (!headersReceived) return 'waiting_for_headers';
      if (firstByteAt === undefined) return 'waiting_for_first_byte';
      return responseEnded ? 'delivering' : 'streaming';
    };
    const progressTimer = lifecycle
      ? setInterval(() => {
          const now = Date.now();
          writeLifecycle('response_progress', {
            statusCode,
            phase: responsePhase(),
            durationMs: now - startedAt,
            ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
            idleMs: now - lastActivityAt,
            bytes,
            chunks,
          });
        }, lifecycle.progressIntervalMs)
      : undefined;
    progressTimer?.unref();
    const stopProgress = () => {
      if (progressTimer) clearInterval(progressTimer);
    };
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const errorType = (err: Error): string => (err as NodeJS.ErrnoException).code ?? err.name;
    let upstream: http.ClientRequest | undefined;
    let attempt = 0;

    // A keep-alive pool hands out sockets the far end may already have closed
    // while they sat idle; the write then fails with a reset before anything is
    // read back. Retrying is safe precisely in that case and only in that case:
    // the socket was reused, so this attempt never reached a fresh connection,
    // and no response has arrived, so the request cannot have been served. A
    // reset on a socket we opened ourselves is a real network fault and is
    // reported as before.
    //
    // `!headersReceived` is load-bearing and reachable. A clean premature close
    // is reported on the response object, but a genuine TCP RST is a socket
    // ERROR, and node's socketErrorListener calls emitErrorEvent(req, err)
    // WITHOUT checking `req.res` -- so it lands here even with half a response
    // already written downstream. Without this term that half-delivered request
    // is replayed into the same stream (pinned by the after-headers test, which
    // resets the raw TCP socket to produce exactly that arrival).
    const isRetryableUpstreamFailure = (err: Error, request: http.ClientRequest): boolean =>
      attempt <= retryBudget
      && !headersReceived
      && !failed
      && !clientDisconnected
      && !isLocalShutdown()
      && request.reusedSocket === true
      && (err as NodeJS.ErrnoException).code === RETRYABLE_PASSTHROUGH_CODE;

    const sendAttempt = (): void => {
      attempt += 1;
      const request = https.request({
        protocol: 'https:',
        hostname: origin.hostname,
        port: origin.port || 443,
        method: req.method,
        path: req.url,
        headers: requestHeadersWithoutProxyHeaders(req),
        servername: net.isIP(origin.hostname) ? undefined : origin.hostname,
        rejectUnauthorized,
        agent,
      }, upstreamRes => {
        headersReceived = true;
        statusCode = upstreamRes.statusCode ?? 502;
        lastActivityAt = Date.now();
        upstreamRes.on('data', (chunk: Buffer) => {
          const now = Date.now();
          if (firstByteAt === undefined) {
            firstByteAt = now;
            writeLifecycle('response_started', {
              statusCode,
              durationMs: now - startedAt,
              timeToFirstByteMs: now - startedAt,
              ...(attempt > 1 ? { attempt } : {}),
            });
          }
          lastActivityAt = now;
          bytes += chunk.length;
          chunks += 1;
        });
        copyResponse(upstreamRes, res, onErrorResponse, onResponseUsage);
        upstreamRes.once('end', () => {
          responseEnded = true;
          lastActivityAt = Date.now();
          done();
        });
        upstreamRes.once('error', err => {
          if (clientDisconnected || failed) {
            done();
            return;
          }
          failed = true;
          stopProgress();
          const now = Date.now();
          writeLifecycle('response_failed', {
            statusCode,
            phase: responsePhase(),
            durationMs: now - startedAt,
            ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
            idleMs: now - lastActivityAt,
            bytes,
            chunks,
            errorType: errorType(err),
            terminationSource: 'upstream_failure',
            attempt,
          });
          done();
        });
      });
      upstream = request;
      request.once('error', err => {
        if (clientDisconnected) {
          done();
          return;
        }
        if (isRetryableUpstreamFailure(err, request)) {
          const retriedAt = Date.now();
          writeLifecycle('response_retried', {
            phase: responsePhase(),
            durationMs: retriedAt - startedAt,
            idleMs: retriedAt - lastActivityAt,
            errorType: errorType(err),
            terminationSource: 'upstream_failure',
            attempt,
            reusedSocket: true,
          });
          lastActivityAt = retriedAt;
          sendAttempt();
          return;
        }
        if (failed) {
          done();
          return;
        }
        failed = true;
        stopProgress();
        const now = Date.now();
        writeLifecycle('response_failed', {
          statusCode: 502,
          phase: responsePhase(),
          durationMs: now - startedAt,
          idleMs: now - lastActivityAt,
          bytes,
          chunks,
          errorType: errorType(err),
          terminationSource: isLocalShutdown() ? 'local_shutdown' : 'upstream_failure',
          attempt,
          reusedSocket: request.reusedSocket === true,
        });
        const detail = upstreamUnreachableDetail(err);
        onErrorResponse?.(502, `Anthropic upstream unreachable: ${detail}`);
        if (!res.headersSent) res.writeHead(502, { 'Content-Type': 'text/plain' });
        res.end(`Anthropic upstream unreachable: ${detail}`);
        done();
      });
      request.end(rawBody);
    };

    res.once('finish', () => {
      stopProgress();
      if (failed || clientDisconnected) return;
      const now = Date.now();
      writeLifecycle('response_completed', {
        statusCode,
        durationMs: now - startedAt,
        ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
        bytes,
        chunks,
        ...(attempt > 1 ? { attempt } : {}),
      });
    });
    res.once('close', () => {
      stopProgress();
      if (res.writableFinished || failed) return;
      clientDisconnected = true;
      const now = Date.now();
      writeLifecycle('response_client_disconnected', {
        statusCode,
        phase: responsePhase(),
        durationMs: now - startedAt,
        ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
        idleMs: now - lastActivityAt,
        bytes,
        chunks,
        terminationSource: isLocalShutdown() ? 'local_shutdown' : 'downstream_client',
      });
      upstream?.destroy(new Error('Client disconnected'));
      done();
    });

    sendAttempt();
  });
}

function forwardToAdapter(
  req: http.IncomingMessage,
  res: http.ServerResponse,
  rawBody: Buffer,
  adapter: ProxyHandle,
  adapterRequest: typeof http.request = http.request,
  adapterAgent?: http.Agent,
  lifecycle?: {
    logPath: string;
    requestId: string;
    claudeSessionId?: string;
    modelId: string;
    provider: string;
    progressIntervalMs: number;
  },
  isLocalShutdown: () => boolean = () => false,
): Promise<void> {
  return new Promise(resolve => {
    const startedAt = Date.now();
    let lastActivityAt = startedAt;
    let headersReceived = false;
    let firstByteAt: number | undefined;
    let statusCode: number | undefined;
    let bytes = 0;
    let chunks = 0;
    let adapterEnded = false;
    let failed = false;
    let clientDisconnected = false;
    let adapterResponse: http.IncomingMessage | undefined;
    let upstream: http.ClientRequest | undefined;

    const writeLifecycle = (
      event: Parameters<typeof writeInferenceResponseLifecycleLog>[1]['event'],
      extra: Partial<Parameters<typeof writeInferenceResponseLifecycleLog>[1]> = {},
    ) => {
      if (!lifecycle) return;
      writeInferenceResponseLifecycleLog(lifecycle.logPath, {
        event,
        requestId: lifecycle.requestId,
        claudeSessionId: lifecycle.claudeSessionId,
        modelId: lifecycle.modelId,
        provider: lifecycle.provider,
        route: 'translated',
        ...extra,
      });
    };
    const responsePhase = (): InferenceResponsePhase => {
      if (!headersReceived) return 'waiting_for_headers';
      if (firstByteAt === undefined) return 'waiting_for_first_byte';
      return adapterEnded ? 'delivering' : 'streaming';
    };
    const progressTimer = lifecycle
      ? setInterval(() => {
          const now = Date.now();
          writeLifecycle('response_progress', {
            statusCode,
            phase: responsePhase(),
            durationMs: now - startedAt,
            ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
            idleMs: now - lastActivityAt,
            bytes,
            chunks,
          });
        }, lifecycle.progressIntervalMs)
      : undefined;
    progressTimer?.unref();
    const stopProgress = () => {
      if (progressTimer) clearInterval(progressTimer);
    };

    res.once('finish', () => {
      stopProgress();
      if (failed || clientDisconnected) return;
      const now = Date.now();
      writeLifecycle('response_completed', {
        statusCode,
        durationMs: now - startedAt,
        ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
        bytes,
        chunks,
      });
    });
    res.once('close', () => {
      stopProgress();
      if (res.writableFinished || failed) return;
      clientDisconnected = true;
      const now = Date.now();
      writeLifecycle('response_client_disconnected', {
        statusCode,
        phase: responsePhase(),
        durationMs: now - startedAt,
        ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
        idleMs: now - lastActivityAt,
        bytes,
        chunks,
        terminationSource: isLocalShutdown() ? 'local_shutdown' : 'downstream_client',
      });
      adapterResponse?.destroy(new Error('Client disconnected'));
      upstream?.destroy(new Error('Client disconnected'));
      resolve();
    });

    const failAdapterRequest = (
      err: Error,
      failureSource: InferenceFailureSource,
    ) => {
      if (clientDisconnected) {
        resolve();
        return;
      }
      if (headersReceived || failed) return;
      failed = true;
      stopProgress();
      const now = Date.now();
      writeLifecycle('response_failed', {
        statusCode: 502,
        phase: responsePhase(),
        durationMs: now - startedAt,
        idleMs: now - lastActivityAt,
        bytes,
        chunks,
        errorType: err.name,
        errorCode: (err as NodeJS.ErrnoException).code,
        failureSource,
        terminationSource: 'upstream_failure',
      });
      if (!res.headersSent) res.writeHead(502, { 'Content-Type': 'text/plain' });
      res.end(`Relay adapter unreachable: ${err.message}`);
      resolve();
    };

    upstream = adapterRequest({
      hostname: '127.0.0.1',
      port: adapter.port,
      method: 'POST',
      path: req.url,
      agent: adapterAgent,
      headers: {
        'Content-Type': 'application/json',
        'Content-Length': String(rawBody.length),
        'x-api-key': adapter.token,
        ...(typeof req.headers['x-claude-code-session-id'] === 'string'
          ? { 'x-claude-code-session-id': req.headers['x-claude-code-session-id'] }
          : {}),
        // Subagent identity: the relay partitions ChatGPT WebSocket heads by it.
        ...(typeof req.headers['x-claude-code-agent-id'] === 'string'
          ? { 'x-claude-code-agent-id': req.headers['x-claude-code-agent-id'] }
          : {}),
        ...(typeof req.headers['x-claude-code-parent-agent-id'] === 'string'
          ? { 'x-claude-code-parent-agent-id': req.headers['x-claude-code-parent-agent-id'] }
          : {}),
        ...(lifecycle ? { 'x-relay-request-id': lifecycle.requestId } : {}),
      },
    }, upstreamRes => {
      adapterResponse = upstreamRes;
      headersReceived = true;
      statusCode = upstreamRes.statusCode ?? 502;
      lastActivityAt = Date.now();
      upstreamRes.on('data', (chunk: Buffer) => {
        const now = Date.now();
        if (firstByteAt === undefined) {
          firstByteAt = now;
          writeLifecycle('response_started', {
            statusCode,
            durationMs: now - startedAt,
            timeToFirstByteMs: now - startedAt,
          });
        }
        lastActivityAt = now;
        bytes += chunk.length;
        chunks += 1;
      });
      copyResponse(upstreamRes, res, undefined, lifecycle
        ? usage => writeLifecycle('response_usage', usage)
        : undefined);
      const failAdapterResponse = (
        err: Error,
        failureSource: InferenceFailureSource,
      ) => {
        if (clientDisconnected) {
          resolve();
          return;
        }
        if (adapterEnded || failed) return;
        failed = true;
        stopProgress();
        const now = Date.now();
        writeLifecycle('response_failed', {
          statusCode,
          phase: responsePhase(),
          durationMs: now - startedAt,
          ...(firstByteAt !== undefined ? { timeToFirstByteMs: firstByteAt - startedAt } : {}),
          idleMs: now - lastActivityAt,
          bytes,
          chunks,
          errorType: err.name,
          errorCode: (err as NodeJS.ErrnoException).code,
          failureSource,
          terminationSource: 'upstream_failure',
        });
        if (!res.writableEnded) res.destroy(err);
        resolve();
      };
      upstreamRes.once('end', () => {
        adapterEnded = true;
        lastActivityAt = Date.now();
        resolve();
      });
      upstreamRes.once('error', err => failAdapterResponse(err, 'adapter_response_error'));
      upstreamRes.once('aborted', () => failAdapterResponse(
        new Error('Relay adapter response aborted'),
        'adapter_response_aborted',
      ));
      upstreamRes.once('close', () => {
        if (!upstreamRes.complete) {
          failAdapterResponse(
            new Error('Relay adapter response closed before completion'),
            'adapter_response_close',
          );
        }
      });
    });
    upstream.once('error', err => failAdapterRequest(err, 'adapter_request_error'));
    upstream.once('close', () => {
      if (!headersReceived && !failed) {
        failAdapterRequest(
          new Error('Relay adapter connection closed before a response'),
          'adapter_request_close',
        );
      }
    });
    upstream.end(rawBody);
  });
}

function forwardAnthropicUpgrade(
  req: http.IncomingMessage,
  clientSocket: Duplex,
  head: Buffer,
  origin: URL,
  rejectUnauthorized: boolean,
  agent: https.Agent | undefined,
  sockets: Set<Socket>,
): void {
  // A pipelined request can still own the socket through an unfinished response.
  // Sharing it would interleave the relay's bytes with that response, and
  // assignSocket would throw, so drop the connection instead.
  if ((clientSocket as Socket & { _httpMessage?: unknown })._httpMessage) {
    clientSocket.destroy();
    return;
  }
  // A paused socket hides EOF while the upstream handshake is pending. Keep
  // reading into a bounded buffer without dropping early client frames.
  const clientData = new PassThrough();
  clientSocket.pipe(clientData);
  let responseStarted = false;
  let upgradedSocket: Socket | undefined;
  const upstream = https.request({
    protocol: 'https:',
    hostname: origin.hostname,
    port: origin.port || 443,
    method: req.method,
    path: req.url,
    headers: requestHeadersWithoutProxyHeaders(req),
    servername: net.isIP(origin.hostname) ? undefined : origin.hostname,
    rejectUnauthorized,
    agent,
  });
  const fail = (): void => {
    if (clientSocket.destroyed) return;
    if (responseStarted) {
      clientSocket.destroy();
      return;
    }
    responseStarted = true;
    clientSocket.end(
      'HTTP/1.1 502 Bad Gateway\r\nConnection: close\r\nContent-Length: 0\r\n\r\n',
      () => clientSocket.destroy(),
    );
  };
  clientSocket.once('error', () => clientSocket.destroy());
  clientSocket.once('end', () => {
    if (!responseStarted) clientSocket.destroy();
  });
  clientSocket.once('close', () => {
    clientData.destroy();
    upstream.destroy();
    upgradedSocket?.destroy();
  });
  upstream.once('socket', socket => {
    if (clientSocket.destroyed) socket.destroy();
  });
  upstream.once('error', fail);
  upstream.once('close', () => {
    if (!responseStarted) fail();
  });
  upstream.once('response', upstreamRes => {
    if (clientSocket.destroyed) {
      upstreamRes.destroy();
      return;
    }
    responseStarted = true;
    // A rejected upgrade is still HTTP; ServerResponse restores chunk framing
    // after IncomingMessage decodes it, instead of sending an invalid raw body.
    const response = new http.ServerResponse(req);
    response.shouldKeepAlive = false;
    response.assignSocket(clientSocket as Socket);
    // Node removes its own drain forwarder from a socket it hands to 'upgrade',
    // so a body larger than the write buffer would stall without this relay.
    clientSocket.on('drain', () => response.emit('drain'));
    response.once('error', () => clientSocket.destroy());
    response.once('finish', () => clientSocket.end(() => clientSocket.destroy()));
    copyResponse(upstreamRes, response);
  });
  upstream.once('upgrade', (upstreamRes, socket, upstreamHead) => {
    if (clientSocket.destroyed) {
      socket.destroy();
      return;
    }
    responseStarted = true;
    // Only upgraded sockets leave request ownership. An HTTP rejection can
    // return its socket to the agent for another request before this client closes.
    upgradedSocket = socket;
    sockets.add(socket);
    socket.once('error', () => clientSocket.destroy());
    socket.once('close', () => {
      sockets.delete(socket);
      clientSocket.destroy();
    });
    const headers = [
      `HTTP/${upstreamRes.httpVersion} ${upstreamRes.statusCode} ${upstreamRes.statusMessage}`,
    ];
    for (let i = 0; i < upstreamRes.rawHeaders.length; i += 2) {
      headers.push(`${upstreamRes.rawHeaders[i]}: ${upstreamRes.rawHeaders[i + 1]}`);
    }
    clientSocket.write(Buffer.from(`${headers.join('\r\n')}\r\n\r\n`, 'latin1'));
    // Either HTTP parser can read beyond the handshake into the first frame.
    if (upstreamHead.length > 0) clientSocket.write(upstreamHead);
    if (head.length > 0) socket.write(head);
    clientData.pipe(socket);
    socket.pipe(clientSocket);
  });
  upstream.end();
}

function forwardPlainHttp(req: http.IncomingMessage, res: http.ServerResponse): void {
  let target: URL;
  try {
    target = new URL(req.url ?? '');
  } catch {
    res.writeHead(400, { 'Content-Type': 'text/plain' });
    res.end('HTTP proxy requests must use an absolute URL');
    return;
  }
  const transport = target.protocol === 'https:' ? https : http;
  const upstream = transport.request({
    protocol: target.protocol,
    hostname: target.hostname,
    port: target.port || undefined,
    method: req.method,
    path: `${target.pathname}${target.search}`,
    headers: requestHeadersWithoutProxyHeaders(req),
  }, upstreamRes => copyResponse(upstreamRes, res));
  upstream.on('error', err => {
    if (!res.headersSent) res.writeHead(502, { 'Content-Type': 'text/plain' });
    res.end(`Proxy upstream unreachable: ${err.message}`);
  });
  req.pipe(upstream);
}

export async function startHttpProxy(options: HttpProxyOptions): Promise<HttpProxyHandle> {
  const certificates = ensureHttpProxyCertificates();
  const routesById = new Map<string, ProxyRoute>();
  const reservedModelIds = new Set<string>();
  for (const route of options.routes) {
    routesById.set(normalizeRouteLookupId(route.aliasId), route);
  }
  for (const alias of options.modelAliases ?? []) {
    const aliasId = normalizeRouteLookupId(alias.name);
    reservedModelIds.add(aliasId);
    for (const sourceName of alias.sourceNames ?? []) {
      reservedModelIds.add(normalizeRouteLookupId(sourceName));
      reservedModelIds.add(normalizeRouteLookupId(sourceName.trim()));
    }
    const route = routesById.get(normalizeRouteLookupId(alias.routeId));
    if (!route) continue;
    routesById.set(aliasId, route);
  }
  for (const modelId of options.reservedModelIds ?? []) {
    reservedModelIds.add(normalizeRouteLookupId(modelId));
  }
  const anthropicOrigin = new URL(options.anthropicOrigin ?? 'https://api.anthropic.com');
  const anthropicProxyUrl = outboundProxyUrlForTarget(anthropicOrigin.href);
  let anthropicAgent: https.Agent | undefined;
  let adapter: ProxyHandle | null = options.adapterHandle ?? null;
  if (options.routes.length > 0) {
    adapter ??= await startProxyCatalog(
      options.routes,
      options.routes[0]!.aliasId,
      options.debug,
      options.inferenceLogPath,
      options.debugLogPath,
      options.webSocketDiagnosticsLogPath,
      options.modelAliases,
    );
  }
  const adapterAgent = adapter ? new http.Agent({ keepAlive: true }) : undefined;
  let shuttingDown = false;

  const mitmServer = https.createServer({
    key: certificates.serverKey,
    cert: certificates.serverCert,
    minVersion: 'TLSv1.2',
  }, async (req, res) => {
    let rawBody: Buffer;
    try {
      rawBody = await readRawBody(req);
    } catch (err) {
      res.writeHead(413, { 'Content-Type': 'text/plain' });
      res.end(err instanceof Error ? err.message : String(err));
      return;
    }

    const messagesEndpoint = anthropicMessagesEndpoint(req.url);
    if (req.method === 'POST' && messagesEndpoint) {
      const requestId = randomUUID();
      let parsed: AnthropicRequest | null = null;
      let route: ProxyRoute | undefined;
      let adapterBody = rawBody;
      const contentEncoding = (Array.isArray(req.headers['content-encoding'])
        ? req.headers['content-encoding'].join(',')
        : req.headers['content-encoding'] ?? '').trim().toLowerCase();
      const encodedRequestBody = contentEncoding !== '' && contentEncoding !== 'identity';
      try {
        const decodedBody = decodeRequestBody(rawBody, req.headers['content-encoding']);
        parsed = JSON.parse(decodedBody) as AnthropicRequest;
        if (encodedRequestBody) adapterBody = Buffer.from(decodedBody);
        if (typeof parsed.model === 'string') {
          route = routesById.get(normalizeRouteLookupId(parsed.model));
        }
      } catch {
        if (encodedRequestBody) {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({
            type: 'error',
            error: {
              type: 'invalid_request_error',
              message: 'Unable to inspect compressed request body',
            },
          }));
          return;
        }
      }
      const claudeSessionIdHeader = Array.isArray(req.headers['x-claude-code-session-id'])
        ? req.headers['x-claude-code-session-id'][0]
        : req.headers['x-claude-code-session-id'];
      const claudeSessionId = parsed
        ? extractClaudeSessionId(parsed, claudeSessionIdHeader)
        : undefined;
      const requestedModel = typeof parsed?.model === 'string' ? parsed.model : undefined;
      const unresolvedRoutedModel = !route && requestedModel !== undefined && (
        normalizeRouteLookupId(requestedModel).startsWith(HTTP_PROXY_MODEL_PREFIX)
        || reservedModelIds.has(normalizeRouteLookupId(requestedModel))
      );

      if (unresolvedRoutedModel) {
        const message = routeUnavailableMessage(requestedModel);
        if (messagesEndpoint === 'messages' && options.inferenceLogPath) {
          writeInferenceRouteUnavailableLog(options.inferenceLogPath, {
            requestId,
            modelId: requestedModel,
            statusCode: 400,
          });
        }
        res.writeHead(400, { 'Content-Type': 'application/json' });
        res.end(JSON.stringify({
          type: 'error',
          error: { type: 'invalid_request_error', message },
        }));
        return;
      }

      if (messagesEndpoint === 'messages' && options.inferenceLogPath) {
        const provider = route
          ? (route.providerId ?? route.aliasId.split(':')[1] ?? 'unknown')
          : 'anthropic';
        writeInferenceRequestLog(options.inferenceLogPath, {
          requestId,
          claudeSessionId,
          modelId: typeof parsed?.model === 'string' ? parsed.model : 'unknown',
          effort: parsed ? anthropicEffortFromRequest(parsed) : undefined,
          // Only for the route that actually carries one, using the same
          // predicate and the same resolver the adapter applies.
          serviceTier: isOpenAiOAuthRoute(route) ? oauthServiceTier() : undefined,
          provider,
          route: route ? 'translated' : 'passthrough',
          stream: Boolean(parsed?.stream),
          requestPreview: getLatestMessagePreview(parsed?.messages, parsed?.system),
        });
      }

      if (messagesEndpoint === 'messages' && options.webSocketDiagnosticsLogPath) {
        const provider = route
          ? (route.providerId ?? route.aliasId.split(':')[1] ?? 'unknown')
          : 'anthropic';
        writeWebSocketDiagnosticRequestLog(options.webSocketDiagnosticsLogPath, {
          requestId,
          claudeSessionId,
          provider,
          route: route ? 'translated' : 'passthrough',
          headers: req.headers,
          body: parsed ? parsed as unknown as Record<string, unknown> : {},
        });
      }

      if (route && adapter) {
        // The adapter resolves alias names itself and must echo the client's
        // requested model id in response messages. Encoded bodies are decoded
        // for this local hop, but their JSON model value is not rewritten.
        // Substituting the canonical route id here broke patched/alias ids in the
        // field. The window lookup does not read the response body in 2.1.261 (see
        // `.claude/docs/claude-code-internals.md`); the echo is about identity.
        await forwardToAdapter(
          req,
          res,
          adapterBody,
          adapter,
          options.adapterRequest,
          adapterAgent,
          messagesEndpoint === 'messages' && options.inferenceLogPath
            ? {
                logPath: options.inferenceLogPath,
                requestId,
                claudeSessionId,
                modelId: typeof parsed?.model === 'string' ? parsed.model : 'unknown',
                provider: route.providerId ?? route.aliasId.split(':')[1] ?? 'unknown',
                progressIntervalMs: options.responseProgressIntervalMs ?? INFERENCE_PROGRESS_INTERVAL_MS,
              }
            : undefined,
          () => shuttingDown,
        );
        return;
      }

      await forwardRawAnthropicRequest(
        req,
        res,
        rawBody,
        anthropicOrigin,
        options.anthropicRejectUnauthorized ?? true,
        anthropicAgent,
        messagesEndpoint === 'messages' && options.inferenceLogPath
          ? (statusCode, errorContent) => writeInferenceResponseErrorLog(options.inferenceLogPath!, {
              requestId,
              modelId: typeof parsed?.model === 'string' ? parsed.model : 'unknown',
              provider: 'anthropic',
              route: 'passthrough',
              statusCode,
              errorContent,
            })
          : undefined,
        messagesEndpoint === 'messages' && options.inferenceLogPath
          ? usage => writeInferenceResponseLifecycleLog(options.inferenceLogPath!, {
              event: 'response_usage',
              requestId,
              claudeSessionId,
              modelId: typeof parsed?.model === 'string' ? parsed.model : 'unknown',
              provider: 'anthropic',
              route: 'passthrough',
              ...usage,
            })
          : undefined,
        messagesEndpoint === 'messages' && options.inferenceLogPath
          ? {
              logPath: options.inferenceLogPath,
              requestId,
              claudeSessionId,
              modelId: typeof parsed?.model === 'string' ? parsed.model : 'unknown',
              provider: 'anthropic',
              progressIntervalMs: options.responseProgressIntervalMs ?? INFERENCE_PROGRESS_INTERVAL_MS,
            }
          : undefined,
        () => shuttingDown,
      );
      return;
    }

    await forwardRawAnthropicRequest(
      req,
      res,
      rawBody,
      anthropicOrigin,
      options.anthropicRejectUnauthorized ?? true,
      anthropicAgent,
    );
  });

  const sockets = new Set<Socket>();
  mitmServer.on('upgrade', (req, socket, head) => {
    forwardAnthropicUpgrade(
      req,
      socket,
      head,
      anthropicOrigin,
      options.anthropicRejectUnauthorized ?? true,
      anthropicAgent,
      sockets,
    );
  });
  const proxyServer = http.createServer(forwardPlainHttp);
  proxyServer.on('connection', socket => {
    sockets.add(socket);
    socket.once('close', () => sockets.delete(socket));
  });
  proxyServer.on('connect', (req, clientSocket, head) => {
    if (shouldInterceptConnect(req.url ?? '')) {
      clientSocket.write('HTTP/1.1 200 Connection Established\r\n\r\n');
      if (head.length > 0) clientSocket.unshift(head);
      mitmServer.emit('connection', clientSocket);
      return;
    }

    // Node's http server drops its own 'error' listener once it hands the
    // socket to 'connect'. Without a replacement a client reset -- or an EPIPE
    // on the 400 write below -- surfaces as an uncaughtException (issue #233).
    clientSocket.once('error', () => clientSocket.destroy());
    const target = authorityParts(req.url ?? '');
    if (!target) {
      clientSocket.end('HTTP/1.1 400 Bad Request\r\n\r\n');
      return;
    }
    const upstream = net.connect(target.port, target.host);
    let tunnelEstablished = false;
    sockets.add(upstream);
    clientSocket.once('close', () => {
      if (!upstream.destroyed) upstream.destroy();
    });
    upstream.once('close', () => {
      sockets.delete(upstream);
      // stream.pipe() ends only the writable half of its destination. Some
      // CONNECT clients allow half-open sockets, so an upstream close would
      // otherwise leave the client socket (and its TLS buffers) retained.
      if (tunnelEstablished && !clientSocket.destroyed) clientSocket.destroy();
    });
    upstream.once('connect', () => {
      tunnelEstablished = true;
      clientSocket.write('HTTP/1.1 200 Connection Established\r\n\r\n');
      if (head.length > 0) upstream.write(head);
      clientSocket.pipe(upstream);
      upstream.pipe(clientSocket);
    });
    upstream.once('error', () => {
      if (clientSocket.destroyed) return;
      if (tunnelEstablished) {
        clientSocket.destroy();
        return;
      }
      clientSocket.end('HTTP/1.1 502 Bad Gateway\r\n\r\n', () => clientSocket.destroy());
    });
  });

  let address: AddressInfo;
  try {
    address = await listenTcpServer(
      proxyServer,
      options.port ?? 0,
      options.host ?? '127.0.0.1',
    );
  } catch (err) {
    adapterAgent?.destroy();
    adapter?.close();
    throw err;
  }

  if (anthropicProxyUrl && proxyUrlTargetsListener(
    anthropicProxyUrl,
    address.address,
    address.port,
  )) {
    console.error(
      'clodex: HTTP(S)_PROXY points at this proxy; sending Anthropic passthrough direct',
    );
  } else {
    anthropicAgent = outboundHttpProxyAgent(anthropicOrigin.href);
  }
  // Without this the passthrough falls back to https.globalAgent, whose pool is
  // shared with every other https client in the process and outlives close().
  anthropicAgent ??= createPassthroughAgent();

  return {
    host: options.host ?? '127.0.0.1',
    port: address.port,
    caCertPath: certificates.caCertPath,
    modelIds: [
      ...(options.modelAliases ?? []).map(alias => alias.name),
      ...options.routes.map(route => route.aliasId),
    ],
    inferenceLogPath: options.inferenceLogPath,
    webSocketDiagnosticsLogPath: options.webSocketDiagnosticsLogPath,
    close: async () => {
      shuttingDown = true;
      for (const socket of sockets) socket.destroy();
      await new Promise<void>(resolve => proxyServer.close(() => resolve()));
      mitmServer.close();
      anthropicAgent?.destroy();
      adapter?.close();
    },
  };
}
